/* eslint-disable @typescript-eslint/no-explicit-any */
// ────────────────────────────────────────────────────────────────────────────
// Accounts payable demo backend (bw-invoices + bw-approver).
//
// Stateful: invoices, approval processes and approval rule sets live in an
// in-memory store mirrored to sessionStorage, so a visitor can upload an
// invoice, edit it, send it for approval, approve / reject / return it, export
// it — and the result survives navigation and reloads within the session.
//
// Shapes follow the generated SDK models (BwInvoicesModel / BwApproverModel)
// and what the bw-2609 payables FE actually reads.
// ────────────────────────────────────────────────────────────────────────────
import {
  DEMO_COMPANIES,
  DEMO_COMPANY_BY_ID,
  type DemoCompany,
} from "./seed/companies.ts";
import {
  DEMO_CLIENT_ROLE_BY_COMPANY,
  DEMO_COMPANY_HOLDINGS_AG,
  DEMO_COMPANY_LABS_SP,
  DEMO_LIVE_PERSON_ID,
  DEMO_USER_ID,
  type DemoCompanyId,
} from "./seed/identifiers.ts";
import { AP_OFFER_COMPANIES } from "./apActivationDemo.ts";
import { generateInvoicePdf } from "./seed/invoicePdf.ts";
import { DEMO_PAYABLES, type DemoPayable } from "./seed/payables.ts";
import { DEMO_TEAM, type DemoTeamMember } from "./seed/team.ts";

type Doc = Record<string, any>;
type Req = { body: Record<string, any>; method: string };

const STORAGE_KEY = "fidoo-demo-payables-v2";
const ANNA_NAME = "Anna Schmidt";
const DEF_VERSION = "1.0.0";

// ── time ────────────────────────────────────────────────────────────────────
// Seed dates were authored around 2026-05-21; shift them so the queue always
// looks current (due dates spread around "today").
const SEED_ANCHOR = Date.parse("2026-05-21T00:00:00.000Z");
const DAY = 86_400_000;
function todayUtc(): number {
  const d = new Date();
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}
function shiftDate(iso: string): string {
  const shifted = Date.parse(`${iso}T00:00:00.000Z`) + (todayUtc() - SEED_ANCHOR);
  return new Date(shifted).toISOString().slice(0, 10);
}
const nowIso = () => new Date().toISOString();
const isoDaysAgo = (days: number) =>
  new Date(Date.now() - days * DAY).toISOString();

const round2 = (n: number) => Math.round(n * 100) / 100;

// ── people ──────────────────────────────────────────────────────────────────
type Person = {
  partyId: string;
  partyRoleId: string;
  name: string;
};

/** Anna as the FE sees "me": livePerson id + her ClientUser role in the company. */
function anna(companyId: DemoCompanyId): Person {
  return {
    partyId: DEMO_LIVE_PERSON_ID,
    partyRoleId: DEMO_CLIENT_ROLE_BY_COMPANY[companyId],
    name: ANNA_NAME,
  };
}

const isAnnaParty = (partyId: string | undefined) =>
  partyId === DEMO_LIVE_PERSON_ID || partyId === DEMO_USER_ID;

function personFromTeam(m: DemoTeamMember, companyId: DemoCompanyId): Person {
  if (m.id === DEMO_USER_ID) return anna(companyId);
  return {
    partyId: m.id,
    partyRoleId: m.partyRoleId,
    name: `${m.firstName} ${m.surname}`,
  };
}

function personByTeamRoleId(
  roleId: string,
  companyId: DemoCompanyId,
): Person | null {
  const m = DEMO_TEAM.find((t) => t.partyRoleId === roleId);
  return m ? personFromTeam(m, companyId) : null;
}

function companyMembers(companyId: DemoCompanyId): DemoTeamMember[] {
  return DEMO_TEAM.filter((m) =>
    m.memberships.some((ms) => ms.companyId === companyId),
  );
}

/** Second approver for the default rule: the company's finance lead. */
function financeLead(companyId: DemoCompanyId): Person | null {
  const m = companyMembers(companyId).find(
    (t) =>
      t.id !== DEMO_USER_ID &&
      (t.jobTitle === "FinancialManager" || t.jobTitle === "Accountant"),
  );
  return m ? personFromTeam(m, companyId) : null;
}

function ceo(companyId: DemoCompanyId): Person | null {
  const m = companyMembers(companyId).find(
    (t) => t.jobTitle === "CEO" || t.jobTitle === "FounderOwner",
  );
  return m ? personFromTeam(m, companyId) : null;
}

/** The FE scopes payables only via requestorIdentity → map it back to a company. */
export function companyFromRequest(body: Record<string, any>): DemoCompanyId | null {
  const roleId =
    body?.requestorIdentity?.requestor?.partyRoleReference?.partyRoleId;
  const hit = (
    Object.entries(DEMO_CLIENT_ROLE_BY_COMPANY) as [DemoCompanyId, string][]
  ).find(([, r]) => r === roleId);
  return hit ? hit[0] : null;
}

// ── store ───────────────────────────────────────────────────────────────────
type InvoiceRec = {
  companyId: DemoCompanyId;
  doc: Doc;
  seq: number;
  /** Seed record used to render the attachment PDF. */
  pdfSeed?: DemoPayable;
  /** Upload in flight: status flips Processing → New after this time. */
  processingUntil?: number;
  isdocXml?: string;
};

type Store = {
  invoices: Record<string, InvoiceRec>;
  processes: Record<string, Doc>;
  definitions: Record<string, Doc>;
  counter: number;
  /** Bulk ISDOC exports (AP-1072 / AP-1074); optional for older session data. */
  exports?: Record<string, ExportRec>;
};

type ExportRec = {
  id: string;
  companyId: DemoCompanyId;
  invoiceIds: string[];
  eligibleIds: string[];
  source: "InvoiceList" | "InvoiceDetail";
  requestedAt: string;
  requestedBy: Person;
  completeAt: number;
  finalized: boolean;
  seq: number;
};

let store: Store | null = null;
/** Uploaded files are kept in memory only (not serialisable). */
const uploadedFiles = new Map<string, Blob>();

function nextId(prefix: string): string {
  const s = getStore();
  s.counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${s.counter}`;
}

function persist() {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Private mode / quota — the in-memory store keeps working.
  }
}

function getStore(): Store {
  if (store) return store;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      store = JSON.parse(raw) as Store;
      return store;
    }
  } catch {
    // ignore
  }
  store = seedStore();
  persist();
  return store;
}

function touch(doc: Doc, seq: number) {
  doc.documentHeader._etag = `"demo-etag-${seq}"`;
  doc.documentHeader._ts = Math.floor(Date.now() / 1000) + seq;
}

function bumpInvoice(rec: InvoiceRec) {
  rec.seq += 1;
  touch(rec.doc, rec.seq);
}

// ── invoice document building ────────────────────────────────────────────────
const VAT_BY_JURISDICTION: Record<DemoCompany["jurisdiction"], number> = {
  AT: 20,
  CZ: 21,
  PL: 23,
  DE: 19,
  GB: 20,
};

const v = <T>(value: T) => ({ value, confidence: 0.97 });

// The FE validations take their rounding tolerance from the data source; the
// real backend always sends one (absent = zero tolerance → every amount rounded
// to cents is flagged as a calculation error).
const OCR_DATA_SOURCE = {
  format: "ocr",
  channel: "manual_upload",
  tolerance: 0.01,
};

type Amounts = {
  total: number; // tax inclusive, local currency
  base: number;
  tax: number;
  rate: number;
};

function splitAmount(total: number, rate: number): Amounts {
  const base = round2(total / (1 + rate / 100));
  return { total, base, tax: round2(total - base), rate };
}

function supplierIco(vat: string): string {
  return vat.replace(/^[A-Z]+/, "").replace(/[^0-9A-Z]/g, "");
}

type ForeignSpec = { currency: string; rate: number };

function buildInvoiceData(
  p: DemoPayable,
  company: DemoCompany,
  foreign?: ForeignSpec,
): Doc {
  const rate = VAT_BY_JURISDICTION[company.jurisdiction];
  // For a foreign invoice the seed amount is in the foreign currency.
  const curr = foreign ? splitAmount(p.amount, rate) : null;
  const local = foreign
    ? splitAmount(round2(p.amount * foreign.rate), rate)
    : splitAmount(p.amount, rate);
  const withCurr = (key: string, l: number, c: number | undefined) =>
    c === undefined
      ? { [key]: v(l) }
      : { [key]: v(l), [`${key}Curr`]: v(c) };

  return {
    id: v(p.invoiceNumber),
    issueDate: v(shiftDate(p.issuedDate)),
    taxPointDate: v(shiftDate(p.issuedDate)),
    documentType: v(1),
    vatApplicable: v(true),
    note: p.notes ? v(p.notes) : undefined,
    localCurrencyCode: v(company.defaultCurrency),
    ...(foreign
      ? {
          foreignCurrencyCode: v(foreign.currency),
          currRate: v(foreign.rate),
          refCurrRate: v(1),
        }
      : {}),
    accountingSupplierParty: {
      party: {
        partyIdentification: { id: v(supplierIco(p.supplierVatNumber)) },
        partyName: { name: v(p.supplierName) },
        partyTaxScheme: { companyID: v(p.supplierVatNumber) },
        postalAddress: {
          streetName: v("Hauptstraße"),
          buildingNumber: v("12"),
          cityName: v(company.city),
          postalZone: v(company.zip),
          country: { identificationCode: v(p.supplierVatNumber.slice(0, 2)) },
        },
      },
    },
    accountingCustomerParty: {
      party: {
        partyIdentification: { id: v(company.registrationNumber) },
        partyName: { name: v(company.name) },
        partyTaxScheme: { companyID: v(company.vatNumber) },
        postalAddress: {
          streetName: v(company.street.replace(/\s+\S+$/, "")),
          buildingNumber: v(company.street.split(" ").pop() ?? ""),
          cityName: v(company.city),
          postalZone: v(company.zip),
          country: { identificationCode: v(company.jurisdiction) },
        },
      },
    },
    taxTotal: {
      ...withCurr("taxAmount", local.tax, curr?.tax),
      taxSubTotal: [
        {
          ...withCurr("taxableAmount", local.base, curr?.base),
          ...withCurr("taxAmount", local.tax, curr?.tax),
          ...withCurr("taxInclusiveAmount", local.total, curr?.total),
          taxCategory: { percent: v(rate), vatApplicable: v(true) },
        },
      ],
    },
    legalMonetaryTotal: {
      ...withCurr("taxExclusiveAmount", local.base, curr?.base),
      ...withCurr("taxInclusiveAmount", local.total, curr?.total),
      ...withCurr("payableAmount", local.total, curr?.total),
    },
    paymentMeans: {
      payment: [
        {
          paymentMeansCode: v(42),
          details: {
            paymentDueDate: v(shiftDate(p.dueDate)),
            iban: v(fakeIban(p.supplierVatNumber)),
            // Bank transfer needs IBAN + BIC (or account no. + bank code).
            bic: v(`DEMO${fakeIban(p.supplierVatNumber).slice(0, 2)}22`),
            variableSymbol: v(p.invoiceNumber.replace(/\D/g, "").slice(-10)),
          },
        },
      ],
    },
  };
}

function fakeIban(seed: string): string {
  const country = /^[A-Z]{2}/.test(seed) ? seed.slice(0, 2) : "CZ";
  const digits = seed.replace(/\D/g, "").padEnd(20, "7").slice(0, 20);
  return `${country}65${digits}`;
}

function buildInvoiceLines(
  p: DemoPayable,
  company: DemoCompany,
  foreign?: ForeignSpec,
): Doc {
  const rate = VAT_BY_JURISDICTION[company.jurisdiction];
  const total = foreign ? round2(p.amount * foreign.rate) : p.amount;
  const a = splitAmount(total, rate);
  const c = foreign ? splitAmount(p.amount, rate) : null;
  const line: Doc = {
    entityHeader: {
      entityType: "InvoiceLine",
      definitionVersion: DEF_VERSION,
      entityId: `${p.id}-line-1`,
      entityContext: "ReqCreateInvoice",
    },
    entityData: {
      id: v("1"),
      invoicedQuantity: v(1),
      unitPrice: v(a.base),
      unitPriceTaxInclusive: v(a.total),
      lineExtensionAmount: v(a.base),
      lineExtensionAmountTaxInclusive: v(a.total),
      lineExtensionTaxAmount: v(a.tax),
      ...(c
        ? {
            lineExtensionAmountCurr: v(c.base),
            lineExtensionAmountTaxInclusiveCurr: v(c.total),
            extensions: { unitPriceCurr: v(c.base) },
          }
        : {}),
      classifiedTaxCategory: {
        percent: v(rate),
        vatCalculationMethod: v(0),
        vatApplicable: v(true),
      },
      item: {
        description: v(p.notes ?? `Services — ${p.invoiceNumber}`),
      },
    },
  };
  return { pagingResponse: { totalItemsCount: 1 }, returnedItems: [line] };
}

function assigneeFields(person: Person | null): Doc {
  if (!person) {
    return {
      assigneeUser: undefined,
      assigneePartyRoleRef: undefined,
      assigneePartyRoleAdditionalInfo: undefined,
    };
  }
  return {
    assigneeUser: { id: person.partyId, roleName: person.name },
    assigneePartyRoleRef: {
      documentId: person.partyId,
      documentType: "LivePerson",
    },
    assigneePartyRoleAdditionalInfo: {
      displayName: person.name,
      roleName: person.name,
    },
  };
}

function uploaderFields(person: Person): Doc {
  return {
    uploaderPartyRoleRef: {
      documentId: person.partyId,
      documentType: "LivePerson",
    },
    uploaderPartyRoleAdditionalInfo: {
      displayName: person.name,
      roleName: person.name,
    },
  };
}

/** Recomputes the server-side display total (AP-741) from invoiceData. */
function recomputeComputedInfo(rec: InvoiceRec) {
  const data = rec.doc.invoiceData?.entityData ?? {};
  const foreign = data.foreignCurrencyCode?.value;
  const local = data.localCurrencyCode?.value;
  const isForeign = Boolean(foreign) && foreign !== local;
  const lmt = data.legalMonetaryTotal ?? {};
  const amount = isForeign
    ? lmt.taxInclusiveAmountCurr?.value
    : lmt.taxInclusiveAmount?.value;
  const created =
    rec.doc.computedInfo?.entityData?.auditMetadata?.entityData?.createdWhen ??
    nowIso();
  rec.doc.computedInfo = {
    entityData: {
      displayTaxInclusiveAmount:
        amount == null
          ? undefined
          : {
              entityData: {
                amount: Number(amount),
                currencyCode: isForeign ? foreign : local,
              },
            },
      auditMetadata: { entityData: { createdWhen: created } },
    },
  };
}

function newInvoiceDoc(id: string): Doc {
  return {
    documentType: "ReqCreateInvoice",
    documentHeader: {
      id,
      documentType: "ReqCreateInvoice",
      definitionVersion: DEF_VERSION,
      _etag: '"demo-etag-0"',
      _ts: Math.floor(Date.now() / 1000),
    },
  };
}

function invoiceFromSeed(
  p: DemoPayable,
  status: string,
  opts: { foreign?: ForeignSpec; createdDaysAgo?: number } = {},
): InvoiceRec {
  const company = DEMO_COMPANY_BY_ID[p.companyId];
  const assignee = p.assignee
    ? personByTeamRoleId(p.assignee.roleId, p.companyId)
    : null;
  const uploader = assignee ?? anna(p.companyId);
  const doc = newInvoiceDoc(p.id);
  doc.invoiceBasicInfo = {
    entityData: {
      status,
      dataSource: OCR_DATA_SOURCE,
      ...assigneeFields(assignee),
      ...uploaderFields(uploader),
    },
  };
  doc.invoiceData = { entityData: buildInvoiceData(p, company, opts.foreign) };
  doc.invoiceLines = buildInvoiceLines(p, company, opts.foreign);
  doc.invoiceAttachment = {
    entityData: {
      fileName: p.attachmentName ?? `${p.invoiceNumber.replace(/\W+/g, "-")}.pdf`,
      contentType: "application/pdf",
      blobName: `${p.id}.pdf`,
    },
  };
  doc.invoiceUserLog = { pagingResponse: { totalItemsCount: 0 }, returnedItems: [] };
  const rec: InvoiceRec = {
    companyId: p.companyId,
    doc,
    seq: 1,
    pdfSeed: p,
  };
  doc.computedInfo = {
    entityData: {
      auditMetadata: {
        entityData: { createdWhen: isoDaysAgo(opts.createdDaysAgo ?? 6) },
      },
    },
  };
  recomputeComputedInfo(rec);
  touch(doc, rec.seq);
  return rec;
}

// ── seed ────────────────────────────────────────────────────────────────────
// Extra invoices that showcase release features the base seed lacks:
// a foreign-currency invoice and corrupted uploads (AP-855).
const EXTRA_SEEDS: { p: DemoPayable; status: string; foreign?: ForeignSpec; reason?: string }[] = [
  {
    p: {
      id: "inv-2026-0407",
      invoiceNumber: "INV-EU-2026-0518",
      supplierName: "Vexa Cloud B.V.",
      supplierVatNumber: "NL864218429B01",
      status: "New",
      assignee: null,
      amount: 1_480,
      currency: "EUR",
      issuedDate: "2026-05-16",
      dueDate: "2026-05-30",
      companyId: DEMO_COMPANY_LABS_SP,
      notes: "Cloud hosting — EU region (invoiced in EUR).",
      attachmentName: "vexa-cloud-eu-2026-05.pdf",
    },
    status: "New",
    foreign: { currency: "EUR", rate: 4.27 },
  },
  {
    p: {
      id: "inv-2026-0348",
      invoiceNumber: "",
      supplierName: "",
      supplierVatNumber: "AT",
      status: "New",
      assignee: null,
      amount: 0,
      currency: "EUR",
      issuedDate: "2026-05-20",
      dueDate: "2026-05-20",
      companyId: DEMO_COMPANY_HOLDINGS_AG,
      attachmentName: "scan-faktura-telefon.jpg",
    },
    status: "Corrupted",
    reason: "ImageResolutionTooLow",
  },
  {
    p: {
      id: "inv-2026-0349",
      invoiceNumber: "",
      supplierName: "",
      supplierVatNumber: "AT",
      status: "New",
      assignee: null,
      amount: 0,
      currency: "EUR",
      issuedDate: "2026-05-19",
      dueDate: "2026-05-19",
      companyId: DEMO_COMPANY_HOLDINGS_AG,
      attachmentName: "rechnung-verschluesselt.pdf",
    },
    status: "Corrupted",
    reason: "PdfEncrypted",
  },
];

function seedStore(): Store {
  const s: Store = { invoices: {}, processes: {}, definitions: {}, counter: 0 };
  store = s;

  // Companies whose AP starts as an offer (Contoso Studio) open empty after
  // activation — no invoices, no rule sets — so the setup can be tried.
  const seeded = (companyId: string) => !AP_OFFER_COMPANIES.includes(companyId);

  // Approval rule sets first — processes for seeded invoices are built from them.
  for (const company of DEMO_COMPANIES.filter((c) => seeded(c.id))) {
    for (const def of seedDefinitions(company.id as DemoCompanyId)) {
      s.definitions[def.documentHeader.id] = def;
    }
  }

  DEMO_PAYABLES.filter((p) => seeded(p.companyId)).forEach((p, i) => {
    // The release flow ends at "Exported" (ISDOC); Posted rows only ever
    // showed under "All", so present them as exported.
    const status = p.status === "Posted" ? "Exported" : p.status;
    const rec = invoiceFromSeed(p, status, { createdDaysAgo: 3 + (i % 9) });
    s.invoices[p.id] = rec;
    if (status === "Exported") attachIsdoc(rec);
  });

  for (const extra of EXTRA_SEEDS.filter((e) => seeded(e.p.companyId))) {
    const rec = invoiceFromSeed(extra.p, extra.status, {
      foreign: extra.foreign,
      createdDaysAgo: 1,
    });
    if (extra.status === "Corrupted") {
      delete rec.doc.invoiceData;
      delete rec.doc.invoiceLines;
      rec.doc.invoiceBasicInfo.entityData.processingReason = extra.reason;
      rec.doc.invoiceBasicInfo.entityData.statusReason = undefined;
      rec.doc.invoiceBasicInfo.entityData = {
        ...rec.doc.invoiceBasicInfo.entityData,
        ...assigneeFields(null),
        ...uploaderFields(anna(extra.p.companyId)),
      };
      rec.pdfSeed = undefined;
      recomputeComputedInfo(rec);
    }
    s.invoices[extra.p.id] = rec;
  }

  // Give in-flight / decided invoices a real approval process so the
  // approval-flow tab, decision banners and approve buttons all work.
  for (const rec of Object.values(s.invoices)) {
    const status = rec.doc.invoiceBasicInfo.entityData.status;
    if (status === "InApproval") {
      startApprovalProcess(rec, { initiator: initiatorFor(rec) });
    } else if (status === "Approved" || status === "Exported") {
      const proc = startApprovalProcess(rec, { initiator: initiatorFor(rec) });
      autoCompleteProcess(proc, rec);
      rec.doc.invoiceBasicInfo.entityData.status = status;
    } else if (status === "Rejected") {
      const proc = startApprovalProcess(rec, { initiator: initiatorFor(rec) });
      const approver = proc.__levels[0]?.users.find(
        (u: Person) => !isAnnaParty(u.partyId),
      ) ?? proc.__levels[0]?.users[0];
      applyDecision(proc, rec, approver, "Rejected", "Missing purchase order reference.");
    }
  }
  return s;
}

function initiatorFor(rec: InvoiceRec): Person {
  const a = rec.doc.invoiceBasicInfo.entityData.assigneeUser;
  if (!a || isAnnaParty(a.id)) return anna(rec.companyId);
  const m = DEMO_TEAM.find((t) => t.id === a.id);
  return m ? personFromTeam(m, rec.companyId) : anna(rec.companyId);
}

// ── ISDOC export ────────────────────────────────────────────────────────────
function attachIsdoc(rec: InvoiceRec) {
  const data = rec.doc.invoiceData?.entityData ?? {};
  const number = data.id?.value ?? rec.doc.documentHeader.id;
  const total = data.legalMonetaryTotal?.taxInclusiveAmount?.value ?? 0;
  const currency = data.localCurrencyCode?.value ?? "CZK";
  rec.isdocXml = `<?xml version="1.0" encoding="UTF-8"?>
<Invoice xmlns="http://isdoc.cz/namespace/2013" version="6.0.2">
  <DocumentType>1</DocumentType>
  <ID>${escapeXml(String(number))}</ID>
  <IssueDate>${data.issueDate?.value ?? ""}</IssueDate>
  <LocalCurrencyCode>${currency}</LocalCurrencyCode>
  <AccountingSupplierParty><Party><PartyName><Name>${escapeXml(
    data.accountingSupplierParty?.party?.partyName?.name?.value ?? "",
  )}</Name></PartyName></Party></AccountingSupplierParty>
  <LegalMonetaryTotal><TaxInclusiveAmount>${total}</TaxInclusiveAmount><PayableAmount>${total}</PayableAmount></LegalMonetaryTotal>
  <Note>Fidoo demo export — illustrative ISDOC.</Note>
</Invoice>
`;
  rec.doc.isdocArtifact = {
    entityData: {
      fileName: `${String(number).replace(/\W+/g, "-") || "invoice"}.isdoc`,
      contentType: "application/xml",
      exportedAt: nowIso(),
    },
  };
}

function escapeXml(s: string) {
  return s.replace(/[<>&"]/g, (c) =>
    c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === "&" ? "&amp;" : "&quot;",
  );
}

// ── approval rule sets (LiveApprovalDefinition / ReqApprovalDefinition) ─────
type LevelSpec = { users: Person[]; required: number; deadlineDays?: number };

function levelsToWire(levels: LevelSpec[]) {
  return levels.map((l, i) => ({
    levelNumber: i + 1,
    approvalsRequiredToProceed: l.required,
    assignedApprovalUsers: l.users.map((u) => ({
      partyRoleReference: {
        partyId: u.partyId,
        partyRoleId: u.partyRoleId,
        partyRoleType: "ClientUser",
      },
    })),
    ...(l.deadlineDays
      ? {
          deadlines: [
            {
              offsetDays: l.deadlineDays,
              offsetCounterType: "FromDate",
              offsetDateType: "ApprovalLevelStartDate",
              priorityMode: "Standard",
            },
          ],
        }
      : {}),
  }));
}

function definitionDoc(
  documentType: "LiveApprovalDefinition" | "ReqApprovalDefinition",
  id: string,
  companyId: DemoCompanyId,
  draft: Doc,
  extra: Doc = {},
): Doc {
  const doc: Doc = {
    documentType,
    documentHeader: {
      id,
      documentType,
      definitionVersion: DEF_VERSION,
      _etag: '"demo-etag-1"',
      _ts: Math.floor(Date.now() / 1000),
    },
    __companyId: companyId,
    basicInfo: {
      entityData: {
        subjectType: "Payable",
        priorityMode: "Standard",
        ...draft.basicInfo,
      },
    },
    ruleDefault: draft.ruleDefault
      ? {
          entityData: {
            data: { entityData: { levels: draft.ruleDefault.levels ?? [] } },
            computedInfo: {
              entityData: {
                approvalType:
                  (draft.ruleDefault.levels ?? []).length === 0
                    ? "Auto"
                    : "Manual",
              },
            },
          },
        }
      : undefined,
    rulesConditional: {
      pagingResponse: { totalItemsCount: (draft.rulesConditional ?? []).length },
      returnedItems: (draft.rulesConditional ?? []).map((r: Doc, i: number) => ({
        entityHeader: {
          entityType: "RuleConditional",
          definitionVersion: DEF_VERSION,
          entityId: `${id}-rule-${i + 1}`,
          entityContext: documentType,
        },
        entityData: {
          data: { entityData: { condition: r.condition, levels: r.levels ?? [] } },
        },
      })),
    },
    lifecycleInfo: { entityData: {} },
    updateHistory: { pagingResponse: { totalItemsCount: 0 }, returnedItems: [] },
    ...extra,
  };
  doc.computedInfo = { entityData: { state: definitionState(doc) } };
  return doc;
}

function definitionState(doc: Doc): string {
  if (doc.documentType === "ReqApprovalDefinition") {
    if (doc.__processState === "Terminated") return "Cancelled";
    return doc.__submitted ? "DraftSubmitted" : "Draft";
  }
  if (doc.lifecycleInfo?.entityData?.cancelledAt) return "Cancelled";
  const now = Date.now();
  const from = Date.parse(doc.basicInfo?.entityData?.validFrom ?? "");
  const to = Date.parse(doc.basicInfo?.entityData?.validTo ?? "");
  if (Number.isFinite(to) && to <= now) return "Expired";
  if (Number.isFinite(from) && from > now) return "Ready";
  return "InUse";
}

function refreshDefinition(doc: Doc) {
  doc.computedInfo = { entityData: { state: definitionState(doc) } };
}

function seedDefinitions(companyId: DemoCompanyId): Doc[] {
  const me = anna(companyId);
  const lead = financeLead(companyId);
  const boss = ceo(companyId);
  const level1: LevelSpec = {
    users: lead ? [me, lead] : [me],
    required: 1,
    deadlineDays: 3,
  };
  const level2: LevelSpec = {
    users: boss && !isAnnaParty(boss.partyId) ? [boss, me] : [me],
    required: 1,
    deadlineDays: 5,
  };
  const company = DEMO_COMPANY_BY_ID[companyId];
  const threshold = company.defaultCurrency === "CZK" ? 100_000 : 5_000;
  const live = definitionDoc(
    "LiveApprovalDefinition",
    `demo-ruleset-${companyId}-standard`,
    companyId,
    {
      basicInfo: {
        name: "Standardní schvalování faktur",
        description:
          "Každá faktura jde ke schválení finančnímu týmu; vyšší částky schvaluje navíc vedení.",
        validFrom: isoDaysAgo(120),
      },
      ruleDefault: { levels: levelsToWire([level1]) },
      rulesConditional: [
        {
          condition: {
            name: `Faktury nad ${threshold.toLocaleString("cs-CZ")} ${company.defaultCurrency}`,
            conditionParts: [
              { conditionType: "accountAmount", operator: ">", value: threshold },
            ],
          },
          levels: levelsToWire([level1, level2]),
        },
      ],
    },
  );
  const draft = definitionDoc(
    "ReqApprovalDefinition",
    `demo-ruleset-${companyId}-draft`,
    companyId,
    {
      basicInfo: {
        name: "Schvalování od příštího roku (koncept)",
        description: "Rozpracovaná pravidla — dvouúrovňové schvalování všech faktur.",
        validFrom: new Date(Date.UTC(new Date().getUTCFullYear() + 1, 0, 1)).toISOString(),
      },
      ruleDefault: { levels: levelsToWire([level1, level2]) },
    },
    { __processState: "Running", masterDocumentReference: { entityData: {} } },
  );
  return [live, draft];
}

/** Picks the rule that applies to an invoice (conditional first, then default). */
function resolveLevels(rec: InvoiceRec): { def: Doc | null; levels: Doc[]; ruleEntityId: string | null } {
  const defs = Object.values(getStore().definitions).filter(
    (d) =>
      d.__companyId === rec.companyId &&
      d.documentType === "LiveApprovalDefinition" &&
      definitionState(d) === "InUse",
  );
  const def = defs[0] ?? null;
  if (!def) return { def: null, levels: [], ruleEntityId: null };
  const data = rec.doc.invoiceData?.entityData ?? {};
  // "accountAmount" conditions compare the tax-exclusive amount in the
  // company's accounting currency (the editor labels it "amount excl. VAT").
  const amount = Number(data.legalMonetaryTotal?.taxExclusiveAmount?.value ?? 0);
  const supplier = String(
    data.accountingSupplierParty?.party?.partyIdentification?.id?.value ?? "",
  );
  const currency = String(data.foreignCurrencyCode?.value ?? data.localCurrencyCode?.value ?? "");
  // Accounting code-list conditions store the attribute code.
  const extensionCode = (key: string) => data.extensions?.[key]?.value?.code ?? "";
  const actualFor: Record<string, () => unknown> = {
    accountAmount: () => amount,
    supplierId: () => supplier,
    currency: () => currency,
    invoiceType: () => String(data.documentType?.value ?? ""),
    costCenter: () => extensionCode("costCenter"),
    contract: () => extensionCode("contract"),
    activity: () => extensionCode("activity"),
    documentSeries: () => extensionCode("documentSeries"),
  };
  const matches = (part: Doc) => {
    const actual: unknown = actualFor[part.conditionType]?.();
    if (actual === undefined) return false;
    const expected = part.value;
    switch (part.operator) {
      case ">": return Number(actual) > Number(expected);
      case ">=": return Number(actual) >= Number(expected);
      case "<": return Number(actual) < Number(expected);
      case "<=": return Number(actual) <= Number(expected);
      case "=": case "==": return String(actual) === String(expected);
      case "!=": return String(actual) !== String(expected);
      case "Contains":
        return String(actual).toLowerCase().includes(String(expected).toLowerCase());
      default: return false;
    }
  };
  for (const item of def.rulesConditional?.returnedItems ?? []) {
    const rule = item.entityData.data.entityData;
    const parts = rule.condition?.conditionParts ?? [];
    if (parts.length > 0 && parts.every(matches)) {
      return { def, levels: rule.levels ?? [], ruleEntityId: item.entityHeader.entityId };
    }
  }
  return {
    def,
    levels: def.ruleDefault?.entityData?.data?.entityData?.levels ?? [],
    ruleEntityId: null,
  };
}

// ── approval processes (LiveApprovalProcess) ─────────────────────────────────
function wireUsersToPeople(level: Doc, companyId: DemoCompanyId): Person[] {
  return (level.assignedApprovalUsers ?? []).map((u: Doc) => {
    const ref = u.partyRoleReference;
    if (isAnnaParty(ref.partyId)) return anna(companyId);
    const m = DEMO_TEAM.find((t) => t.id === ref.partyId);
    return m
      ? personFromTeam(m, companyId)
      : { partyId: ref.partyId, partyRoleId: ref.partyRoleId, name: ref.partyId };
  });
}

const partyRef = (p: Person) => ({
  partyId: p.partyId,
  partyRoleId: p.partyRoleId,
  partyRoleType: "ClientUser",
});

function startApprovalProcess(rec: InvoiceRec, { initiator }: { initiator: Person }): Doc {
  const s = getStore();
  const id = nextId("demo-approval");
  const { def, levels, ruleEntityId } = resolveLevels(rec);
  const company = DEMO_COMPANY_BY_ID[rec.companyId];
  const data = rec.doc.invoiceData?.entityData ?? {};
  const display = rec.doc.computedInfo?.entityData?.displayTaxInclusiveAmount?.entityData;
  const previousId = rec.doc.invoiceBasicInfo.entityData.approvalProcessRef?.id;
  if (previousId && s.processes[previousId] && !s.processes[previousId].__result) {
    s.processes[previousId].__result = { status: "Restarted", createdAt: nowIso() };
  }
  const proc: Doc = {
    documentType: "LiveApprovalProcess",
    documentHeader: {
      id,
      documentType: "LiveApprovalProcess",
      definitionVersion: DEF_VERSION,
      _etag: '"demo-etag-1"',
      _ts: Math.floor(Date.now() / 1000),
    },
    __companyId: rec.companyId,
    __invoiceId: rec.doc.documentHeader.id,
    __seq: 1,
    __createdAt: nowIso(),
    __initiator: initiator,
    __levels: levels.map((l: Doc) => ({
      levelNumber: l.levelNumber,
      required: l.approvalsRequiredToProceed,
      users: wireUsersToPeople(l, rec.companyId),
    })),
    __states: [] as Doc[],
    __result: null as Doc | null,
    approvalDefinitionSnapshot: {
      entityData: {
        basicInfo: def?.basicInfo?.entityData ?? { name: "Automatické schválení" },
      },
    },
    approvalDefinitionRef: def
      ? { entityData: { documentId: def.documentHeader.id, documentType: "LiveApprovalDefinition" } }
      : undefined,
    previousApprovalProcessRef: previousId
      ? { entityData: { documentId: previousId, documentType: "LiveApprovalProcess" } }
      : undefined,
    initiatorPartyRoleRef: { entityData: partyRef(initiator) },
    initiatorInfo: { entityData: { firstName: initiator.name.split(" ")[0], surname: initiator.name.split(" ").slice(1).join(" ") } },
    processRuleDefinition: { entityData: { ruleEntityId, ruleLevels: levels } },
    subjectData: {
      entityData: {
        created: nowIso(),
        supplier: data.accountingSupplierParty?.party?.partyName?.name?.value ?? "",
        supplierId: data.accountingSupplierParty?.party?.partyIdentification?.id?.value ?? "",
        subjectId: rec.doc.documentHeader.id,
        subjectNumber: data.id?.value ?? "",
        receiver: company.name,
        receiverId: company.registrationNumber,
        amount: Number(display?.amount ?? 0),
        currency: display?.currencyCode ?? company.defaultCurrency,
        accountAmount: Number(data.legalMonetaryTotal?.taxInclusiveAmount?.value ?? 0),
        accountAmountDescaled: Number(data.legalMonetaryTotal?.taxInclusiveAmount?.value ?? 0),
        accountCurrency: company.defaultCurrency,
        dueDate: data.paymentMeans?.payment?.[0]?.details?.paymentDueDate?.value ?? "",
        invoiceType: "Invoice",
        description: data.note?.value,
      },
    },
  };
  s.processes[id] = proc;
  rec.doc.invoiceBasicInfo.entityData.approvalProcessRef = {
    id,
    documentType: "LiveApprovalProcess",
  };
  if (proc.__levels.length === 0) {
    proc.__result = { status: "Approved", createdAt: nowIso() };
    rec.doc.invoiceBasicInfo.entityData.status = "Approved";
  } else {
    openLevel(proc, 0);
    rec.doc.invoiceBasicInfo.entityData.status = "InApproval";
  }
  return proc;
}

function openLevel(proc: Doc, index: number) {
  const level = proc.__levels[index];
  proc.__states.push({
    levelNumber: level.levelNumber,
    createdAt: nowIso(),
    requiredPartyApprovals: level.users.map(partyRef),
    responses: [] as Doc[],
    status: "InProgress",
  });
}

function currentState(proc: Doc): Doc | undefined {
  if (proc.__result) return undefined;
  return proc.__states.find((st: Doc) => st.status === "InProgress");
}

/** Seeded Approved/Exported invoices: every level approved by its first approver. */
function autoCompleteProcess(proc: Doc, rec: InvoiceRec) {
  let guard = 0;
  while (!proc.__result && guard < 10) {
    guard += 1;
    const st = currentState(proc);
    if (!st) break;
    const level = proc.__levels.find((l: Doc) => l.levelNumber === st.levelNumber);
    const approver = level.users.find((u: Person) => !isAnnaParty(u.partyId)) ?? level.users[0];
    applyDecision(proc, rec, approver, "Approved");
  }
}

function addUserLog(rec: InvoiceRec, author: Person, newStatus: string, reason?: string): string {
  const entryId = nextId("demo-log");
  const log = rec.doc.invoiceUserLog ?? { pagingResponse: { totalItemsCount: 0 }, returnedItems: [] };
  log.returnedItems.push({
    entityHeader: {
      entityType: "InvoiceUserLog",
      definitionVersion: DEF_VERSION,
      entityId: entryId,
      entityContext: "ReqCreateInvoice",
    },
    entityData: {
      timestamp: nowIso(),
      author: { partyId: author.partyId, partyRoleId: author.partyRoleId },
      authorInfo: { requestor: { name: author.name } },
      statusChanges: [{ newStatus, reason }],
    },
  });
  log.pagingResponse = { totalItemsCount: log.returnedItems.length };
  rec.doc.invoiceUserLog = log;
  return entryId;
}

function applyDecision(
  proc: Doc,
  rec: InvoiceRec,
  approver: Person,
  status: "Approved" | "Rejected" | "ChangesRequested",
  comment?: string,
) {
  const st = currentState(proc);
  if (!st) return;
  st.responses.push({
    party: partyRef(approver),
    response: { status, comment, createdAt: nowIso() },
  });
  const level = proc.__levels.find((l: Doc) => l.levelNumber === st.levelNumber);
  const info = rec.doc.invoiceBasicInfo.entityData;

  if (status === "Approved") {
    const approvals = st.responses.filter((r: Doc) => r.response.status === "Approved").length;
    if (approvals >= level.required) {
      st.status = "Approved";
      st.result = { status: "Approved", createdAt: nowIso() };
      const nextIndex = proc.__levels.findIndex((l: Doc) => l.levelNumber === st.levelNumber) + 1;
      if (nextIndex < proc.__levels.length) {
        openLevel(proc, nextIndex);
      } else {
        proc.__result = { status: "Approved", createdAt: nowIso() };
        info.status = "Approved";
        info.statusReason = undefined;
        addUserLog(rec, approver, "Approved");
      }
    }
  } else if (status === "Rejected") {
    st.status = "Rejected";
    st.result = { status: "Rejected", createdAt: nowIso() };
    proc.__result = { status: "Rejected", createdAt: nowIso() };
    info.status = "Rejected";
    info.statusReason = "ApprovalRejected";
    info.rejectLogRef = { logEntryId: addUserLog(rec, approver, "Rejected", comment) };
  } else {
    st.status = "PendingChangesRequested";
    st.changesRequestedBy = partyRef(approver);
    info.status = "NeedsCorrection";
    info.returnLogRef = { logEntryId: addUserLog(rec, approver, "NeedsCorrection", comment) };
  }
  proc.__seq += 1;
  touch(proc, proc.__seq);
}

/** Serialises the internal process state into the SDK read shape. */
function processView(proc: Doc): Doc {
  const st = currentState(proc);
  const me = st?.requiredPartyApprovals.find((r: Doc) => isAnnaParty(r.partyId));
  const responded = st?.responses.some((r: Doc) => isAnnaParty(r.party.partyId));
  const actions: string[] = [];
  if (st && st.status === "InProgress" && me && !responded) {
    actions.push("ApproverSubmitApproval", "ApproverSubmitRejection", "ApproverSubmitChangesRequested");
  }
  if (!proc.__result && st && isAnnaParty(proc.__initiator?.partyId)) {
    actions.push("ApproverCancelProcess");
  }
  const levelsComputed = proc.__states.map((s: Doc) => {
    const level = proc.__levels.find((l: Doc) => l.levelNumber === s.levelNumber);
    return {
      levelNumber: s.levelNumber,
      status: s.status,
      approvalsRequiredToProceed: level?.required ?? 1,
      approvals: s.responses
        .filter((r: Doc) => r.response.status === "Approved")
        .map((r: Doc) => ({ partyRoleRef: r.party })),
      ...(s.changesRequestedBy ? { changesRequestedBy: { partyRoleRef: s.changesRequestedBy } } : {}),
      ...(s.result ? { result: s.result } : {}),
    };
  });
  return {
    documentType: proc.documentType,
    documentHeader: proc.documentHeader,
    approvalDefinitionRef: proc.approvalDefinitionRef,
    // The approvals inbox resolves the company from the snapshot's party ref.
    approvalDefinitionSnapshot: {
      entityData: {
        ...proc.approvalDefinitionSnapshot.entityData,
        partyRoleRef: {
          documentId: proc.__companyId,
          documentType: "LiveCompany",
        },
      },
    },
    previousApprovalProcessRef: proc.previousApprovalProcessRef,
    initiatorPartyRoleRef: proc.initiatorPartyRoleRef,
    initiatorInfo: proc.initiatorInfo,
    processRuleDefinition: proc.processRuleDefinition,
    subjectData: proc.subjectData,
    executionState: {
      entityData: {
        ruleLevelStates: proc.__states.map((s: Doc) => ({
          levelNumber: s.levelNumber,
          createdAt: s.createdAt,
          requiredPartyApprovals: s.requiredPartyApprovals,
          responses: s.responses,
          ...(s.result ? { result: s.result } : {}),
        })),
        ...(proc.__result ? { result: proc.__result } : {}),
      },
    },
    computedInfo: {
      entityData: {
        ruleEntityId: proc.processRuleDefinition.entityData.ruleEntityId,
        levels: levelsComputed,
        ...(proc.__result ? { result: proc.__result } : {}),
      },
    },
    availableActions: actions,
  };
}

// ── list helpers ─────────────────────────────────────────────────────────────
function paging(body: Record<string, any>) {
  const p = body?.paging ?? {};
  const pageSize = Number(p.pageSize ?? 20);
  const start =
    p.startWith != null
      ? Number(p.startWith)
      : (Number(p.pageNumber ?? 1) - 1) * pageSize;
  return { start, pageSize };
}

function pagedList(items: Doc[], body: Record<string, any>) {
  const { start, pageSize } = paging(body);
  return {
    documentList: items.slice(start, start + pageSize),
    pagingResponse: {
      totalItemsCount: items.length,
      hasNextPage: start + pageSize < items.length,
      startWith: start,
      pageSize,
    },
  };
}

function filterValue(filtering: Doc[] | undefined, attr: string): any {
  return (filtering ?? []).find((f) => f.filteringAttribute === attr)?.attributeValue;
}

function invoiceView(rec: InvoiceRec): Doc {
  // Lazily finish an upload's "OCR" once its processing time is over.
  if (rec.processingUntil && Date.now() >= rec.processingUntil) {
    finishProcessing(rec);
  }
  return rec.doc;
}

function invoicesForRequest(body: Record<string, any>): InvoiceRec[] {
  const companyId = companyFromRequest(body);
  return Object.values(getStore().invoices).filter(
    (r) => !companyId || r.companyId === companyId,
  );
}

const SORT_KEYS: Record<string, (d: Doc) => string | number> = {
  supplierName: (d) => d.invoiceData?.entityData?.accountingSupplierParty?.party?.partyName?.name?.value ?? "",
  invoiceNumber: (d) => d.invoiceData?.entityData?.id?.value ?? "",
  status: (d) => d.invoiceBasicInfo?.entityData?.status ?? "",
  assigneeName: (d) => d.invoiceBasicInfo?.entityData?.assigneeUser?.roleName ?? "",
  dueDate: (d) => d.invoiceData?.entityData?.paymentMeans?.payment?.[0]?.details?.paymentDueDate?.value ?? "",
  displayAmount: (d) => Number(d.computedInfo?.entityData?.displayTaxInclusiveAmount?.entityData?.amount ?? 0),
  createdWhen: (d) => d.computedInfo?.entityData?.auditMetadata?.entityData?.createdWhen ?? "",
  fileName: (d) => d.invoiceAttachment?.entityData?.fileName ?? "",
};

function listInvoices(req: Req) {
  const body = req.body;
  const filtering: Doc[] = body.filtering ?? [];
  const statuses: string[] | undefined = filterValue(filtering, "statusArray");
  const reasons: string[] | undefined = filterValue(filtering, "statusReasonArray");
  const search = String(filterValue(filtering, "search") ?? "").trim().toLowerCase();
  const suppliers: string[] | undefined = filterValue(filtering, "supplierIcoArray");
  const assignees: string[] | undefined = filterValue(filtering, "assigneeArray");
  const dueFrom = filterValue(filtering, "dueDateFrom");
  const dueTo = filterValue(filtering, "dueDateTo");
  const displayCurrency = filterValue(filtering, "displayCurrency");
  const amountFrom = filterValue(filtering, "displayAmountFrom");
  const amountTo = filterValue(filtering, "displayAmountTo");
  const ids: string[] | undefined =
    filterValue(filtering, "documentIdArray") ??
    (filterValue(filtering, "documentId") ? [filterValue(filtering, "documentId")] : undefined);

  let docs = invoicesForRequest(body).map(invoiceView);
  docs = docs.filter((d) => {
    const info = d.invoiceBasicInfo?.entityData ?? {};
    const data = d.invoiceData?.entityData ?? {};
    const display = d.computedInfo?.entityData?.displayTaxInclusiveAmount?.entityData;
    const due = data.paymentMeans?.payment?.[0]?.details?.paymentDueDate?.value;
    if (ids && !ids.includes(d.documentHeader.id)) return false;
    if (statuses?.length && !statuses.includes(info.status)) return false;
    if (reasons?.length && !reasons.includes(info.statusReason)) return false;
    if (suppliers?.length && !suppliers.includes(data.accountingSupplierParty?.party?.partyIdentification?.id?.value)) return false;
    if (assignees?.length) {
      const id = info.assigneeUser?.id;
      const match = id ? assignees.includes(id) || (isAnnaParty(id) && assignees.some(isAnnaParty)) : assignees.includes("Unassigned");
      if (!match) return false;
    }
    if (dueFrom && (!due || due < dueFrom)) return false;
    if (dueTo && (!due || due > dueTo)) return false;
    if (displayCurrency && display?.currencyCode !== displayCurrency) return false;
    if (amountFrom != null && Number(display?.amount ?? 0) < Number(amountFrom)) return false;
    if (amountTo != null && Number(display?.amount ?? 0) > Number(amountTo)) return false;
    if (search) {
      const hay = [
        data.id?.value,
        data.accountingSupplierParty?.party?.partyName?.name?.value,
        data.accountingSupplierParty?.party?.partyIdentification?.id?.value,
        d.invoiceAttachment?.entityData?.fileName,
      ].join(" ").toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });

  const sorting: Doc[] = body.sorting ?? [{ sortingAttribute: "createdWhen", order: "DESC" }];
  const sort = sorting[0];
  const key = SORT_KEYS[sort?.sortingAttribute] ?? SORT_KEYS.createdWhen;
  const dir = String(sort?.order ?? "DESC").toUpperCase() === "ASC" ? 1 : -1;
  docs.sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    return (ka > kb ? 1 : ka < kb ? -1 : 0) * dir;
  });
  return pagedList(docs, body);
}

function findInvoice(body: Record<string, any>): InvoiceRec | undefined {
  const id =
    body?.documentReference?.documentId ??
    body?.updateDocumentReference?.documentId ??
    body?.deleteDocumentReference?.documentId ??
    body?.documentId;
  return id ? getStore().invoices[id] : undefined;
}

function invoiceResponse(rec: InvoiceRec): Doc {
  return invoiceView(rec);
}

// ── upload ──────────────────────────────────────────────────────────────────
const OCR_SUPPLIER_POOL: Record<DemoCompanyId, DemoPayable[]> = (() => {
  const by = {} as Record<DemoCompanyId, DemoPayable[]>;
  for (const p of DEMO_PAYABLES) (by[p.companyId] ??= []).push(p);
  return by;
})();

function createInvoice(req: Req) {
  const companyId = companyFromRequest(req.body) ?? DEMO_COMPANY_HOLDINGS_AG;
  const id = nextId("demo-inv");
  const doc = newInvoiceDoc(id);
  const me = anna(companyId);
  doc.invoiceBasicInfo = {
    entityData: { status: "Created", dataSource: OCR_DATA_SOURCE, ...assigneeFields(me), ...uploaderFields(me) },
  };
  // No invoiceData / invoiceLines until "OCR" finishes — like the real
  // backend; empty entities fail the SDK response validation.
  doc.invoiceUserLog = { pagingResponse: { totalItemsCount: 0 }, returnedItems: [] };
  const rec: InvoiceRec = { companyId, doc, seq: 1 };
  recomputeComputedInfo(rec);
  touch(doc, 1);
  getStore().invoices[id] = rec;
  persist();
  return invoiceResponse(rec);
}

function uploadAttachment(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  const meta = req.body.createEntity?.invoiceAttachment?.entityData ?? {};
  const file = req.body.__file as Blob | undefined;
  const fileName = meta.fileName ?? (file as File | undefined)?.name ?? "faktura.pdf";
  rec.doc.invoiceAttachment = {
    entityData: {
      fileName,
      contentType: meta.contentType ?? file?.type ?? "application/pdf",
      fileSize: meta.fileSize ?? file?.size,
      blobName: `${rec.doc.documentHeader.id}-upload`,
    },
  };
  if (file) uploadedFiles.set(rec.doc.documentHeader.id, file);
  rec.doc.invoiceBasicInfo.entityData.status = "Processing";
  rec.processingUntil = Date.now() + 3_500;
  rec.doc.computedInfo.entityData.auditMetadata.entityData.createdWhen = nowIso();
  bumpInvoice(rec);
  persist();
  return invoiceResponse(rec);
}

/** Simulated OCR: fill the invoice with plausible data for the company. */
function finishProcessing(rec: InvoiceRec) {
  rec.processingUntil = undefined;
  const pool = OCR_SUPPLIER_POOL[rec.companyId] ?? DEMO_PAYABLES;
  const template = pool[Math.floor(Math.random() * pool.length)];
  const company = DEMO_COMPANY_BY_ID[rec.companyId];
  const number = `${template.invoiceNumber.replace(/\d+$/, "")}${String(Math.floor(1000 + Math.random() * 8999))}`;
  const amount = Math.round(template.amount * (0.6 + Math.random() * 0.8));
  const fake: DemoPayable = {
    ...template,
    id: rec.doc.documentHeader.id,
    invoiceNumber: number,
    amount,
    issuedDate: new Date(SEED_ANCHOR - 2 * DAY).toISOString().slice(0, 10),
    dueDate: new Date(SEED_ANCHOR + 12 * DAY).toISOString().slice(0, 10),
    notes: template.notes,
  };
  rec.doc.invoiceData = { entityData: buildInvoiceData(fake, company) };
  rec.doc.invoiceLines = buildInvoiceLines(fake, company);
  rec.doc.invoiceBasicInfo.entityData.status = "New";
  rec.pdfSeed = fake;
  recomputeComputedInfo(rec);
  bumpInvoice(rec);
  persist();
}

// ── invoice mutations ───────────────────────────────────────────────────────
function updateInvoice(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  const all = req.body.updateEntity?.invoiceAllData?.entityData;
  if (all?.invoiceData) {
    const incoming = all.invoiceData.entityData ?? all.invoiceData;
    rec.doc.invoiceData = { entityData: { ...(rec.doc.invoiceData?.entityData ?? {}), ...incoming } };
  }
  if (all?.invoiceLines) {
    const lines = Array.isArray(all.invoiceLines) ? all.invoiceLines : all.invoiceLines.returnedItems ?? [];
    rec.doc.invoiceLines = {
      pagingResponse: { totalItemsCount: lines.length },
      returnedItems: lines.map((l: Doc, i: number) => ({
        entityHeader: {
          entityType: "InvoiceLine",
          definitionVersion: DEF_VERSION,
          entityId: l.entityHeader?.entityId ?? l.entityId ?? `${rec.doc.documentHeader.id}-line-${i + 1}`,
          entityContext: "ReqCreateInvoice",
        },
        entityData: l.entityData ?? l,
      })),
    };
  }
  const info = rec.doc.invoiceBasicInfo.entityData;
  if (info.status === "New") info.status = "Draft";
  recomputeComputedInfo(rec);
  bumpInvoice(rec);
  persist();
  return invoiceResponse(rec);
}

function setStatus(rec: InvoiceRec, status: string) {
  rec.doc.invoiceBasicInfo.entityData.status = status;
  bumpInvoice(rec);
  persist();
}

function submitForApproval(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  // Like production: the approver cannot resolve any rule set in use for
  // this invoice → the send is rejected and the invoice stays a draft.
  if (!resolveLevels(rec).def) {
    return {
      __mockError: {
        status: 400,
        data: {
          error: "BadRequest",
          errorCode: "NoApplicableApprovalDefinition",
          message: "No approval definition available for the invoice.",
        },
      },
    };
  }
  const info = rec.doc.invoiceBasicInfo.entityData;
  info.returnLogRef = undefined;
  info.statusReason = undefined;
  startApprovalProcess(rec, { initiator: anna(rec.companyId) });
  addUserLog(rec, anna(rec.companyId), info.status);
  bumpInvoice(rec);
  persist();
  return invoiceResponse(rec);
}

function cancelApproval(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  const procId = rec.doc.invoiceBasicInfo.entityData.approvalProcessRef?.id;
  const proc = procId ? getStore().processes[procId] : undefined;
  if (proc && !proc.__result) {
    const st = currentState(proc);
    if (st) {
      st.status = "Cancelled";
      st.result = { status: "Cancelled", createdAt: nowIso() };
    }
    proc.__result = { status: "Cancelled", createdAt: nowIso() };
    proc.__seq += 1;
    touch(proc, proc.__seq);
  }
  addUserLog(rec, anna(rec.companyId), "Draft", req.body.reason);
  setStatus(rec, "Draft");
  return invoiceResponse(rec);
}

function exportIsdoc(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  attachIsdoc(rec);
  addUserLog(rec, anna(rec.companyId), "Exported");
  setStatus(rec, "Exported");
  return invoiceResponse(rec);
}

function assignToMe(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  Object.assign(rec.doc.invoiceBasicInfo.entityData, assigneeFields(anna(rec.companyId)));
  bumpInvoice(rec);
  persist();
  return invoiceResponse(rec);
}

function unassignMe(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  Object.assign(rec.doc.invoiceBasicInfo.entityData, assigneeFields(null));
  bumpInvoice(rec);
  persist();
  return invoiceResponse(rec);
}

function deleteInvoice(req: Req) {
  const rec = findInvoice(req.body);
  if (rec) {
    delete getStore().invoices[rec.doc.documentHeader.id];
    uploadedFiles.delete(rec.doc.documentHeader.id);
    persist();
  }
  return { documentHeader: rec?.doc.documentHeader ?? {} };
}

/** Blank invoice skeleton for manual entry: customer + currency known, the rest is up to the user. */
function blankInvoiceData(companyId: DemoCompanyId): Doc {
  const company = DEMO_COMPANY_BY_ID[companyId];
  const full = buildInvoiceData(
    {
      id: "",
      invoiceNumber: "",
      supplierName: "",
      supplierVatNumber: "",
      status: "Draft",
      assignee: null,
      amount: 0,
      currency: company.defaultCurrency,
      issuedDate: new Date(SEED_ANCHOR).toISOString().slice(0, 10),
      dueDate: new Date(SEED_ANCHOR + 14 * DAY).toISOString().slice(0, 10),
      companyId,
    },
    company,
  );
  return {
    documentType: full.documentType,
    vatApplicable: full.vatApplicable,
    localCurrencyCode: full.localCurrencyCode,
    issueDate: full.issueDate,
    accountingCustomerParty: full.accountingCustomerParty,
  };
}

function enterDetailsManually(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  const info = rec.doc.invoiceBasicInfo.entityData;
  info.processingReason = undefined;
  rec.doc.invoiceData = { entityData: blankInvoiceData(rec.companyId) };
  rec.doc.invoiceLines = { pagingResponse: { totalItemsCount: 0 }, returnedItems: [] };
  recomputeComputedInfo(rec);
  Object.assign(info, assigneeFields(anna(rec.companyId)));
  setStatus(rec, "Draft");
  return invoiceResponse(rec);
}

function genericInvoiceAck(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  bumpInvoice(rec);
  persist();
  return invoiceResponse(rec);
}

function rejectDocument(req: Req) {
  const rec = findInvoice(req.body);
  if (!rec) return { error: "not found" };
  rec.doc.invoiceBasicInfo.entityData.statusReason = req.body.statusReason;
  setStatus(rec, "Rejected");
  return invoiceResponse(rec);
}

function filterOptions(req: Req) {
  const categories: string[] = req.body.categories ?? ["supplier", "assignee"];
  const recs = invoicesForRequest(req.body).map(invoiceView);
  const suppliers = new Map<string, string>();
  const assignees = new Map<string, string>();
  for (const d of recs) {
    const party = d.invoiceData?.entityData?.accountingSupplierParty?.party;
    const ico = party?.partyIdentification?.id?.value;
    if (ico) suppliers.set(ico, party?.partyName?.name?.value ?? ico);
    const a = d.invoiceBasicInfo?.entityData?.assigneeUser;
    if (a?.id) assignees.set(a.id, a.roleName ?? a.id);
  }
  return {
    ...(categories.includes("supplier")
      ? { supplierList: [...suppliers].map(([id, name]) => ({ id, name })) }
      : {}),
    ...(categories.includes("assignee")
      ? { assigneeList: [...assignees].map(([id, name]) => ({ id, name })) }
      : {}),
  };
}

// ── bulk ISDOC export (RC11: AP-1072 export a selection, AP-1074 export list) ─
const EXPORT_ELIGIBLE_STATUSES = new Set(["Approved", "Exported"]);
const EXPORT_MAX_BATCH = 500;
const EXPORT_DURATION_MS = 4_000;
const EXPORT_RETENTION_DAYS = 90;

function exportsStore(): Record<string, ExportRec> {
  const s = getStore();
  s.exports ??= {};
  return s.exports;
}

function eligibleInvoiceIds(ids: string[]): string[] {
  return ids.filter((id) => {
    const status = getStore().invoices[id]?.doc.invoiceBasicInfo.entityData.status;
    return EXPORT_ELIGIBLE_STATUSES.has(status);
  });
}

function getExportEligibility(req: Req) {
  const ids: string[] = req.body?.invoiceIds ?? [];
  return {
    requestedCount: ids.length,
    eligibleCount: eligibleInvoiceIds(ids).length,
    maxBatchSize: EXPORT_MAX_BATCH,
  };
}

function createExport(req: Req) {
  const ids: string[] = req.body?.entityData?.invoiceIds ?? [];
  const firstRec = ids.map((id) => getStore().invoices[id]).find(Boolean);
  const companyId = (companyFromRequest(req.body) ?? firstRec?.companyId ?? DEMO_COMPANY_HOLDINGS_AG) as DemoCompanyId;
  const id = nextId("demo-export");
  const rec: ExportRec = {
    id,
    companyId,
    invoiceIds: ids,
    eligibleIds: eligibleInvoiceIds(ids),
    source: ids.length === 1 ? "InvoiceDetail" : "InvoiceList",
    requestedAt: nowIso(),
    requestedBy: anna(companyId),
    completeAt: Date.now() + EXPORT_DURATION_MS,
    finalized: false,
    seq: 1,
  };
  exportsStore()[id] = rec;
  persist();
  return exportView(rec);
}

/** Lazily completes the export once its simulated processing time is over. */
function finalizeExport(rec: ExportRec) {
  if (rec.finalized || Date.now() < rec.completeAt) return;
  for (const invoiceId of rec.eligibleIds) {
    const inv = getStore().invoices[invoiceId];
    if (!inv) continue;
    attachIsdoc(inv);
    if (inv.doc.invoiceBasicInfo.entityData.status !== "Exported") {
      addUserLog(inv, rec.requestedBy, "Exported");
      setStatus(inv, "Exported");
    }
  }
  rec.finalized = true;
  rec.seq += 1;
  persist();
}

function exportFileName(rec: ExportRec): string {
  return `isdoc-export-${rec.requestedAt.slice(0, 10)}-${rec.id.slice(-4)}.zip`;
}

function exportView(rec: ExportRec): Doc {
  finalizeExport(rec);
  const total = rec.eligibleIds.length;
  const elapsed = Math.min(1, (Date.now() - (rec.completeAt - EXPORT_DURATION_MS)) / EXPORT_DURATION_MS);
  const processed = rec.finalized ? total : Math.floor(total * elapsed);
  const header = (entityType: string) => ({
    entityType,
    definitionVersion: DEF_VERSION,
    entityContext: "ReqInvoiceExport",
  });
  return {
    documentType: "ReqInvoiceExport",
    documentHeader: {
      id: rec.id,
      documentType: "ReqInvoiceExport",
      definitionVersion: DEF_VERSION,
      _ts: Math.floor(Date.parse(rec.requestedAt) / 1000) + rec.seq + processed,
      _etag: `"demo-export-${rec.seq}-${processed}"`,
    },
    exportRequest: {
      entityData: {
        source: rec.source,
        requestedScope: { invoiceIds: rec.invoiceIds },
        requestedAt: rec.requestedAt,
        requestedByName: rec.requestedBy.name,
      },
      entityHeader: header("ExportRequest"),
    },
    requestedBy: {
      entityData: { documentId: rec.requestedBy.partyId, documentType: "LivePerson" },
      entityHeader: header("RequestedBy"),
    },
    partyRoleRef: {
      entityData: { documentId: rec.companyId, documentType: "LiveCompany" },
      entityHeader: header("PartyRoleRef"),
    },
    summary: {
      entityData: {
        totalInvoices: total,
        processed,
        generated: processed,
        reused: 0,
        failed: 0,
      },
      entityHeader: header("ExportSummary"),
    },
    ...(rec.finalized && total > 0
      ? {
          exportFile: {
            entityData: {
              blobName: `exports/${rec.id}.zip`,
              fileName: exportFileName(rec),
              contentType: "application/zip",
              fileSize: 2048 * total,
              blobStorageUrl: `demo://exports/${rec.id}.zip`,
              generatedAt: new Date(rec.completeAt).toISOString(),
              expiresAt: new Date(Date.parse(rec.requestedAt) + EXPORT_RETENTION_DAYS * 86_400_000).toISOString(),
            },
            entityHeader: header("ExportFile"),
            availableActions: ["this.downloadAttachment"],
          },
        }
      : {}),
    processingInfo: {
      entityData: {
        processingBasicInfo: {
          entityData: {
            processState: rec.finalized ? (total > 0 ? "Completed" : "Terminated") : "Running",
          },
          entityHeader: { entityType: "ProcessingBasicInfo", definitionVersion: DEF_VERSION, entityContext: "ReqInvoiceExport" },
        },
      },
    },
  };
}

function readExport(req: Req) {
  const id = req.body?.documentReference?.documentId;
  const rec = id ? exportsStore()[id] : undefined;
  return rec ? exportView(rec) : { documentList: [] };
}

function listExports(req: Req) {
  const companyId = companyFromRequest(req.body);
  const filtering: Doc[] = req.body?.filtering ?? [];
  const views = Object.values(exportsStore())
    .filter((e) => !companyId || e.companyId === companyId)
    .map(exportView);
  const search = String(filterValue(filtering, "search") ?? "").trim().toLowerCase();
  const statuses = filterValue(filtering, "statusArray");
  const authors = filterValue(filtering, "requestedByArray");
  let docs = views.filter((d) => {
    if (search && !JSON.stringify(d.exportRequest.entityData).toLowerCase().includes(search) && !String(d.exportFile?.entityData?.fileName ?? "").toLowerCase().includes(search)) return false;
    if (Array.isArray(statuses) && statuses.length && !statuses.includes(d.processingInfo.entityData.processingBasicInfo.entityData.processState)) return false;
    if (Array.isArray(authors) && authors.length && !authors.includes(d.requestedBy.entityData.documentId)) return false;
    return true;
  });
  docs = docs.sort((a, b) => (a.exportRequest.entityData.requestedAt < b.exportRequest.entityData.requestedAt ? 1 : -1));
  return pagedList(docs, req.body);
}

/** Minimal STORE-only ZIP (no compression) so the archive opens anywhere. */
function buildZip(files: { name: string; content: string }[]): Blob {
  const enc = new TextEncoder();
  const crcTable = Array.from({ length: 256 }, (_, n) => {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    return c >>> 0;
  });
  const crc32 = (bytes: Uint8Array) => {
    let c = 0xffffffff;
    for (const b of bytes) c = crcTable[(c ^ b) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
  };
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const f of files) {
    const name = enc.encode(f.name);
    const data = enc.encode(f.content);
    const crc = crc32(data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true); // UTF-8 names
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true);
    chunks.push(new Uint8Array(local.buffer), name, data);
    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true);
    cd.setUint16(4, 20, true);
    cd.setUint16(6, 20, true);
    cd.setUint16(8, 0x0800, true);
    cd.setUint32(16, crc, true);
    cd.setUint32(20, data.length, true);
    cd.setUint32(24, data.length, true);
    cd.setUint16(28, name.length, true);
    cd.setUint32(42, offset, true);
    central.push(new Uint8Array(cd.buffer), name);
    offset += 30 + name.length + data.length;
  }
  const centralSize = central.reduce((n, c) => n + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  return new Blob([...chunks, ...central, new Uint8Array(end.buffer)] as BlobPart[], { type: "application/zip" });
}

function exportArchive(exportId: string): Blob {
  const rec = exportsStore()[exportId];
  const files = (rec?.eligibleIds ?? []).flatMap((invoiceId) => {
    const inv = getStore().invoices[invoiceId];
    if (!inv) return [];
    if (!inv.isdocXml) attachIsdoc(inv);
    return [{ name: inv.doc.isdocArtifact?.entityData?.fileName ?? `${invoiceId}.isdoc`, content: inv.isdocXml ?? "" }];
  });
  return buildZip(files);
}

// ── attachments (two-step download) ──────────────────────────────────────────
const PENDING_DOWNLOADS = new Map<string, { invoiceId: string; entity: string }>();

function downloadAttachment(req: Req) {
  if (req.method.toUpperCase() === "POST") {
    const invoiceId = req.body?.documentReference?.documentId;
    const entity = Object.keys(req.body?.readEntities ?? {})[0] ?? "invoiceAttachment";
    const requestId = `demo-download-${invoiceId ?? "x"}-${Date.now()}`;
    if (invoiceId) PENDING_DOWNLOADS.set(requestId, { invoiceId, entity });
    return { requestId };
  }
  const requestId = req.body?.["request-id"] ?? req.body?.requestId;
  const pending = requestId ? PENDING_DOWNLOADS.get(requestId) : undefined;
  return { __payablesBinary: pending ?? { invoiceId: Object.keys(getStore().invoices)[0], entity: "invoiceAttachment" } };
}

/**
 * Called by the axios adapter: turns a `__payablesBinary` marker into the
 * actual file (uploaded file, generated PDF, or ISDOC XML).
 */
export async function resolvePayablesBinary(
  payload: unknown,
): Promise<{ blob: Blob; contentType: string } | null> {
  const marker = (payload as { __payablesBinary?: { invoiceId: string; entity: string } } | null)?.__payablesBinary;
  if (!marker) return null;
  if (marker.entity === "exportFile") {
    return { blob: exportArchive(marker.invoiceId), contentType: "application/zip" };
  }
  const rec = getStore().invoices[marker.invoiceId];
  if (marker.entity === "isdocArtifact") {
    const xml = rec?.isdocXml ?? (rec ? (attachIsdoc(rec), rec.isdocXml) : "<Invoice/>");
    return { blob: new Blob([xml ?? ""], { type: "application/xml" }), contentType: "application/xml" };
  }
  const uploaded = uploadedFiles.get(marker.invoiceId);
  if (uploaded) {
    return { blob: uploaded, contentType: uploaded.type || "application/pdf" };
  }
  if (rec && !rec.pdfSeed) {
    return { blob: unreadableScanPdf(rec.doc.invoiceAttachment?.entityData?.fileName), contentType: "application/pdf" };
  }
  const seed: DemoPayable =
    rec?.pdfSeed ??
    DEMO_PAYABLES.find((p) => p.id === marker.invoiceId) ??
    DEMO_PAYABLES[0];
  const blob = await generateInvoicePdf({
    ...seed,
    issuedDate: shiftDate(seed.issuedDate),
    dueDate: shiftDate(seed.dueDate),
  });
  return { blob, contentType: "application/pdf" };
}

/** Stand-in for a corrupted upload: a one-page PDF that is visibly unusable. */
function unreadableScanPdf(fileName: string | undefined): Blob {
  const label = (fileName ?? "scan").replace(/[()\\]/g, "");
  const content = `BT /F1 16 Tf 0.6 g 72 760 Td (${label}) Tj ET\nBT /F1 11 Tf 0.7 g 72 736 Td (Nahrany soubor nelze precist - demo nahled.) Tj ET\n0.85 g 72 200 451 500 re f\n`;
  const pdf = [
    "%PDF-1.4",
    "1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj",
    "2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj",
    "3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 595 842]/Contents 4 0 R/Resources<</Font<</F1 5 0 R>>>>>>endobj",
    `4 0 obj<</Length ${content.length}>>stream\n${content}endstream endobj`,
    "5 0 obj<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>endobj",
    "trailer<</Root 1 0 R>>",
    "%%EOF",
  ].join("\n");
  return new Blob([pdf], { type: "application/pdf" });
}

// ── bw-approver handlers ──────────────────────────────────────────────────────
function approverGetDocumentList(req: Req) {
  const read = req.body.readDocuments ?? {};
  if (read.LiveApprovalProcess) return listProcesses(req);
  return listDefinitions(req);
}

function listProcesses(req: Req) {
  const companyId = companyFromRequest(req.body);
  const filtering: Doc[] = req.body.readDocuments.LiveApprovalProcess.filtering ?? req.body.filtering ?? [];
  const id = filterValue(filtering, "documentId");
  const assigned = filterValue(filtering, "approverAssignedLevelState");
  let procs = Object.values(getStore().processes);
  if (id) procs = procs.filter((p) => p.documentHeader.id === id);
  else if (companyId) procs = procs.filter((p) => p.__companyId === companyId);
  if (assigned) {
    procs = procs.filter((p) => {
      const st = currentState(p);
      return (
        st &&
        st.status === "InProgress" &&
        st.requiredPartyApprovals.some((r: Doc) => isAnnaParty(r.partyId)) &&
        !st.responses.some((r: Doc) => isAnnaParty(r.party.partyId))
      );
    });
  }
  const search = String(filterValue(filtering, "substringSearch") ?? "").toLowerCase();
  if (search) {
    procs = procs.filter((p) =>
      JSON.stringify(p.subjectData.entityData).toLowerCase().includes(search),
    );
  }
  // Approvals inbox filter panel (SC-739 / SC-780).
  const contains = (value: unknown, needle: unknown) =>
    String(value ?? "").toLowerCase().includes(String(needle).trim().toLowerCase());
  const supplier = filterValue(filtering, "subjectSupplier");
  if (supplier) procs = procs.filter((p) => contains(p.subjectData.entityData.supplier, supplier));
  const receiver = filterValue(filtering, "subjectReceiver");
  if (receiver) procs = procs.filter((p) => contains(p.subjectData.entityData.receiver, receiver));
  const initiator = filterValue(filtering, "initiatorFullName");
  if (initiator) procs = procs.filter((p) => contains(p.__initiator?.name, initiator));
  const amountMin = filterValue(filtering, "subjectAmountGreaterThanOrEqual");
  if (amountMin != null) procs = procs.filter((p) => Number(p.subjectData.entityData.amount) >= Number(amountMin));
  const amountMax = filterValue(filtering, "subjectAmountLessThanOrEqual");
  if (amountMax != null) procs = procs.filter((p) => Number(p.subjectData.entityData.amount) <= Number(amountMax));
  const dueFrom = filterValue(filtering, "subjectDueDateGreaterThanOrEqual");
  if (dueFrom) procs = procs.filter((p) => String(p.subjectData.entityData.dueDate ?? "").slice(0, 10) >= String(dueFrom));
  const dueTo = filterValue(filtering, "subjectDueDateLessThanOrEqual");
  if (dueTo) procs = procs.filter((p) => {
    const due = String(p.subjectData.entityData.dueDate ?? "").slice(0, 10);
    return due !== "" && due <= String(dueTo);
  });
  const types = filterValue(filtering, "subjectTypeArray");
  if (Array.isArray(types) && types.length) {
    procs = procs.filter((p) =>
      types.includes(p.approvalDefinitionSnapshot?.entityData?.basicInfo?.subjectType ?? "Payable"),
    );
  }
  procs.sort((a, b) => (a.__createdAt < b.__createdAt ? 1 : -1));
  return pagedList(procs.map(processView), req.body);
}

function definitionMatches(doc: Doc, filtering: Doc[] | null | undefined): boolean {
  if (filtering === null) return false;
  for (const f of filtering ?? []) {
    const val = f.attributeValue;
    const info = doc.basicInfo?.entityData ?? {};
    const from = Date.parse(info.validFrom ?? "") || 0;
    const to = Date.parse(info.validTo ?? "") || Number.POSITIVE_INFINITY;
    switch (f.filteringAttribute) {
      case "documentId":
        if (doc.documentHeader.id !== val) return false;
        break;
      case "definitionSubstringSearch":
        if (!`${info.name ?? ""} ${info.description ?? ""}`.toLowerCase().includes(String(val).toLowerCase())) return false;
        break;
      case "definitionIsCancelled":
        if (Boolean(doc.lifecycleInfo?.entityData?.cancelledAt) !== Boolean(val)) return false;
        break;
      case "definitionValidFromGreaterThan":
        if (!(from > Date.parse(val))) return false;
        break;
      case "definitionValidFromLessThanOrEqual":
        if (!(from <= Date.parse(val))) return false;
        break;
      case "definitionValidToGreaterThan":
        if (!(to > Date.parse(val))) return false;
        break;
      case "definitionValidToLessThanOrEqual":
        if (!(to <= Date.parse(val))) return false;
        break;
      case "processState":
        if ((doc.__processState ?? "Running") !== val) return false;
        break;
      case "processStateArray":
        if (!(val as string[]).includes(doc.__processState ?? "Running")) return false;
        break;
      default:
        break;
    }
  }
  return true;
}

function listDefinitions(req: Req) {
  const companyId = companyFromRequest(req.body);
  const read = req.body.readDocuments ?? {};
  const all = Object.values(getStore().definitions).filter(
    (d) => !companyId || d.__companyId === companyId,
  );
  all.forEach(refreshDefinition);
  const out: Doc[] = [];
  if (read.LiveApprovalDefinition) {
    out.push(...all.filter((d) => d.documentType === "LiveApprovalDefinition" && definitionMatches(d, read.LiveApprovalDefinition.filtering)));
  }
  if (read.ReqApprovalDefinition) {
    out.push(...all.filter((d) => d.documentType === "ReqApprovalDefinition" && definitionMatches(d, read.ReqApprovalDefinition.filtering)));
  }
  // A submitted draft is represented by its live successor in lists.
  const visible = out.filter(
    (d) => !(d.documentType === "ReqApprovalDefinition" && d.__processState === "Terminated" && d.masterDocumentReference?.entityData?.documentId),
  );
  visible.sort((a, b) => (a.documentHeader._ts < b.documentHeader._ts ? 1 : -1));
  return pagedList(visible, req.body);
}

function approverCreateDocument(req: Req) {
  const companyId = companyFromRequest(req.body) ?? DEMO_COMPANY_HOLDINGS_AG;
  const s = getStore();
  if (req.body.documentType === "ReqApprovalDefinitionValidityUpdate") {
    const masterId = req.body.masterDocumentReference?.documentId ?? req.body.entityData?.masterDocumentReference?.documentId;
    const live = masterId ? s.definitions[masterId] : undefined;
    if (live) {
      const info = req.body.entityData?.basicInfo ?? req.body.entityData ?? {};
      if ("validFrom" in info) live.basicInfo.entityData.validFrom = info.validFrom;
      if ("validTo" in info) live.basicInfo.entityData.validTo = info.validTo ?? undefined;
      bumpDefinition(live);
    }
    persist();
    return live ?? { documentHeader: { id: nextId("demo-validity"), documentType: req.body.documentType } };
  }
  const id = nextId("demo-ruleset");
  const doc = definitionDoc("ReqApprovalDefinition", id, companyId, req.body.entityData ?? {}, {
    __processState: "Running",
    masterDocumentReference: { entityData: {} },
  });
  s.definitions[id] = doc;
  persist();
  return doc;
}

function bumpDefinition(doc: Doc) {
  const seq = Number(String(doc.documentHeader._etag).replace(/\D/g, "")) + 1 || 2;
  touch(doc, seq);
  refreshDefinition(doc);
}

function approverUpdateEntity(req: Req) {
  const ref = req.body.updateDocumentReference ?? {};
  const s = getStore();
  if (req.body.documentType === "LiveApprovalProcess" || ref.documentType === "LiveApprovalProcess") {
    return submitApprovalResponse(req);
  }
  const doc = s.definitions[ref.documentId];
  if (!doc) return { error: "not found" };
  const upd = req.body.updateEntity ?? {};
  if (upd.basicInfo) {
    doc.basicInfo.entityData = { ...doc.basicInfo.entityData, ...(upd.basicInfo.entityData ?? upd.basicInfo) };
    if (doc.basicInfo.entityData.validTo === null) delete doc.basicInfo.entityData.validTo;
  }
  if (upd.ruleDefault) {
    const levels = upd.ruleDefault.updateEntity?.data?.entityData?.levels ?? upd.ruleDefault.levels ?? [];
    doc.ruleDefault = { entityData: { data: { entityData: { levels } } } };
  }
  if (upd.rulesConditional) {
    const item = doc.rulesConditional.returnedItems.find((r: Doc) => r.entityHeader.entityId === upd.rulesConditional.entityId);
    const data = upd.rulesConditional.updateEntity?.data?.entityData;
    if (item && data) item.entityData.data.entityData = { ...item.entityData.data.entityData, ...data };
  }
  const action = upd.updateAction?.entityData?.action;
  if (action === "Submit" && doc.documentType === "ReqApprovalDefinition") {
    const liveId = nextId("demo-ruleset-live");
    const draft = {
      basicInfo: doc.basicInfo.entityData,
      ruleDefault: doc.ruleDefault ? { levels: doc.ruleDefault.entityData.data.entityData.levels } : undefined,
      rulesConditional: doc.rulesConditional.returnedItems.map((r: Doc) => r.entityData.data.entityData),
    };
    const live = definitionDoc("LiveApprovalDefinition", liveId, doc.__companyId, draft);
    if (!live.basicInfo.entityData.validFrom) live.basicInfo.entityData.validFrom = nowIso();
    refreshDefinition(live);
    // Only one rule set applies at a time: the new one ends the validity of
    // any set of the same company still running from its start date on.
    const newFrom = live.basicInfo.entityData.validFrom;
    for (const other of Object.values(s.definitions)) {
      if (
        other.documentType === "LiveApprovalDefinition" &&
        other.__companyId === doc.__companyId &&
        !other.lifecycleInfo?.entityData?.cancelledAt &&
        Date.parse(other.basicInfo.entityData.validFrom ?? "") < Date.parse(newFrom) &&
        !(Date.parse(other.basicInfo.entityData.validTo ?? "") <= Date.parse(newFrom))
      ) {
        other.basicInfo.entityData.validTo = newFrom;
        bumpDefinition(other);
      }
    }
    s.definitions[liveId] = live;
    doc.__processState = "Terminated";
    doc.__submitted = true;
    doc.masterDocumentReference = { entityData: { documentId: liveId, documentType: "LiveApprovalDefinition" } };
  }
  if (action === "Cancel") {
    if (doc.documentType === "LiveApprovalDefinition") {
      doc.lifecycleInfo = { entityData: { cancelledAt: nowIso() } };
    } else {
      doc.__processState = "Terminated";
    }
  }
  bumpDefinition(doc);
  persist();
  return doc;
}

function approverCreateEntity(req: Req) {
  const ref = req.body.updateDocumentReference ?? {};
  const doc = getStore().definitions[ref.documentId];
  if (!doc) return { error: "not found" };
  const ce = req.body.createEntity ?? {};
  if (ce.rulesConditional) {
    const data = ce.rulesConditional.entityData?.data?.entityData ?? ce.rulesConditional.data ?? ce.rulesConditional;
    doc.rulesConditional.returnedItems.push({
      entityHeader: {
        entityType: "RuleConditional",
        definitionVersion: DEF_VERSION,
        entityId: nextId(`${doc.documentHeader.id}-rule`),
        entityContext: doc.documentType,
      },
      entityData: { data: { entityData: { condition: data.condition, levels: data.levels ?? [] } } },
    });
    doc.rulesConditional.pagingResponse = { totalItemsCount: doc.rulesConditional.returnedItems.length };
  }
  if (ce.ruleDefault) {
    const levels = ce.ruleDefault.entityData?.data?.entityData?.levels ?? ce.ruleDefault.levels ?? [];
    doc.ruleDefault = { entityData: { data: { entityData: { levels } } } };
  }
  bumpDefinition(doc);
  persist();
  return doc;
}

function approverDeleteEntity(req: Req) {
  const ref = req.body.updateDocumentReference ?? {};
  const doc = getStore().definitions[ref.documentId];
  if (!doc) return { error: "not found" };
  const de = req.body.deleteEntity ?? {};
  if (de.rulesConditional) {
    doc.rulesConditional.returnedItems = doc.rulesConditional.returnedItems.filter(
      (r: Doc) => r.entityHeader.entityId !== de.rulesConditional.entityId,
    );
    doc.rulesConditional.pagingResponse = { totalItemsCount: doc.rulesConditional.returnedItems.length };
  }
  if (de.ruleDefault) doc.ruleDefault = undefined;
  bumpDefinition(doc);
  persist();
  return doc;
}

function submitApprovalResponse(req: Req) {
  const s = getStore();
  const procId = req.body.updateDocumentReference?.documentId;
  const proc = procId ? s.processes[procId] : undefined;
  if (!proc) return { error: "not found" };
  const data = req.body.updateEntity?.approvalResponse?.entityData?.data ?? {};
  const rec = s.invoices[proc.__invoiceId];
  if (rec) {
    applyDecision(proc, rec, anna(rec.companyId), data.status ?? "Approved", data.comment);
    bumpInvoice(rec);
  }
  persist();
  return processView(proc);
}

// ── registry ────────────────────────────────────────────────────────────────
type Handler = (req: { body: Record<string, unknown>; method: string }) => unknown;
// Always hand out copies: returning store objects by reference lets React
// Query cache the live object, so later in-place mutations look "unchanged"
// on the next poll and the UI never re-renders.
const h =
  (fn: (req: Req) => unknown): Handler =>
  (req) => {
    const result = fn(req as Req);
    return result === undefined ? result : JSON.parse(JSON.stringify(result));
  };

export const PAYABLES_HANDLERS: Record<string, Handler> = {
  "bw-invoices/getDocumentList": h((req) =>
    req.body?.readDocuments?.ReqInvoiceExport ? listExports(req) : listInvoices(req),
  ),
  "bw-invoices/getExportEligibility": h(getExportEligibility),
  "bw-invoices/readEntities": h((req) => {
    if (req.body?.documentType === "ReqInvoiceExport") return readExport(req);
    const rec = findInvoice(req.body);
    return rec ? invoiceResponse(rec) : { documentList: [] };
  }),
  "bw-invoices/getInvoiceFilterOptions": h(filterOptions),
  "bw-invoices/createDocument": h((req) =>
    req.body?.documentType === "ReqInvoiceExport" ? createExport(req) : createInvoice(req),
  ),
  "bw-invoices/uploadAttachment": h(uploadAttachment),
  "bw-invoices/updateEntity": h(updateInvoice),
  "bw-invoices/submitForApproval": h(submitForApproval),
  "bw-invoices/cancelApproval": h(cancelApproval),
  "bw-invoices/exportInvoiceIsdoc": h(exportIsdoc),
  "bw-invoices/assignToMe": h(assignToMe),
  "bw-invoices/unassignMe": h(unassignMe),
  "bw-invoices/deleteDocument": h(deleteInvoice),
  "bw-invoices/enterDetailsManually": h(enterDetailsManually),
  "bw-invoices/dismissFlag": h(genericInvoiceAck),
  "bw-invoices/dismissDuplicate": h(genericInvoiceAck),
  "bw-invoices/resolveDuplicate": h(genericInvoiceAck),
  "bw-invoices/rejectDocument": h(rejectDocument),
  "bw-invoices/downloadAttachment": h(downloadAttachment),
  "bw-approver/getDocumentList": h(approverGetDocumentList),
  "bw-approver/createDocument": h(approverCreateDocument),
  "bw-approver/updateEntity": h(approverUpdateEntity),
  "bw-approver/createEntity": h(approverCreateEntity),
  "bw-approver/deleteEntity": h(approverDeleteEntity),
};

/** Test/demo helper: wipe payables state (e.g. from the demo reset button). */
export function resetPayablesDemoState() {
  store = null;
  uploadedFiles.clear();
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
